import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  Ref,
  MouseEvent,
  PropsWithoutRef
} from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import { reflow } from '../../utils';
import { BaseProps, PropsWithDefaults, ForwardProps } from '../../types';
import Flex, { FlexProps } from '../Flex';

export interface BackdropProps extends BaseProps {
  /**
   * Opacity of the Backdrop.
   * @default 0.4
   */
  alpha?: number;
  /**
   * Any React Node to be rendered on top of the Backdrop component.
   */
  children?: ReactNode;
  /** Props passed to Flex container. */
  container?: Exclude<FlexProps['container'], boolean>;
  /**
   * Toggles visibility of Backdrop.
   * @default false
   */
  open?: boolean;
  /**
   * Determines the color of the Backdrop.
   * @default "dark"
   */
  variant?: 'dark' | 'light';
  /**
   * Transition speed of Backdrop animation.
   * @default "medium"
   */
  transitionSpeed?: 'slow' | 'medium' | 'fast' | 'none';
  /**
   * The position of the Backdrop.
   * @default "fixed"
   */
  position?: 'absolute' | 'fixed';
  /**
   * Accepts a callback function for click event on Backdrop.
   */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  /**
   * Accepts a callback that gets invoked before opening Backdrop.
   */
  onBeforeTransitionIn?: () => void;
  /**
   * Accepts a callback that gets invoked after opening Backdrop.
   */
  onAfterTransitionIn?: () => void;
  /**
   * Accepts a callback that gets invoked before closing Backdrop.
   */
  onBeforeTransitionOut?: () => void;
  /**
   * Accepts a callback that gets invoked after closing Backdrop
   */
  onAfterTransitionOut?: () => void;
  /** Ref for a Backdrop's root div element. */
  ref?: Ref<HTMLDivElement>;
}

type BackdropPropsWithDefaults = PropsWithDefaults<
  BackdropProps,
  'open' | 'transitionSpeed' | 'alpha' | 'position'
>;

const transitionSpeedToTheme = {
  slow: 2,
  medium: 1,
  fast: 0.5,
  none: 0
} as const;

export const StyledBackdrop = styled.div<BackdropPropsWithDefaults & { opacity: 0 | 1 }>(props => {
  const {
    theme: { base }
  } = props;
  const { opacity, alpha, variant, position } = props;
  const transitionDuration = `max(calc(${transitionSpeedToTheme[props.transitionSpeed]} * ${
    base.animation.speed
  }), 1ms)`;

  return css`
    position: ${position};
    z-index: ${position === 'fixed' ? base['z-index'].backdrop : base['z-index'].popover + 1};
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    border-radius: inherit;
    opacity: ${opacity};
    transition-property: opacity;
    transition-timing-function: ${props.theme.base.animation.timing.ease};

    /* stylelint-disable declaration-block-no-duplicate-properties */
    transition-duration: 1ms;
    transition-duration: ${transitionDuration};

    /* stylelint-enable declaration-block-no-duplicate-properties */

    ${variant === 'dark' &&
    css`
      background: rgba(0, 0, 0, ${alpha});
    `}

    ${variant === 'light' &&
    css`
      background: rgba(255, 255, 255, ${alpha});
    `}
  `;
});

StyledBackdrop.defaultProps = defaultThemeProp;

type State = 'closed' | 'opening' | 'open' | 'closing';

const Backdrop: FunctionComponent<BackdropProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<BackdropProps>, ref: BackdropProps['ref']) => {
    const {
      children,
      container,
      open = false,
      variant = 'dark',
      transitionSpeed = 'medium',
      alpha = 0.6,
      position = 'fixed',
      onBeforeTransitionIn,
      onAfterTransitionIn,
      onBeforeTransitionOut,
      onAfterTransitionOut,
      ...restProps
    } = props;

    const [state, setState] = useState<State>('closed');

    const onTransitionEnd = useCallback(
      (e: TransitionEvent) => {
        if (e.propertyName !== 'opacity' || e.target !== e.currentTarget) return;
        let nextState: State;

        if (state === 'closing') {
          nextState = 'closed';
          onAfterTransitionOut?.();
        } else {
          nextState = 'open';
          onAfterTransitionIn?.();
        }

        setState(nextState);
      },
      [state, onAfterTransitionOut, onAfterTransitionIn]
    );

    useEffect(() => {
      if (open) {
        if (state === 'closed' || state === 'closing') {
          onBeforeTransitionIn?.();
          reflow();
          setState('opening');
        }
      } else if (state === 'open' || state === 'opening') {
        onBeforeTransitionOut?.();
        setState('closing');
      }
    }, [open, onBeforeTransitionIn, onBeforeTransitionOut]);

    return !open && state === 'closed' ? null : (
      <Flex
        container={{ justify: 'center', alignItems: 'center', ...container }}
        as={StyledBackdrop}
        transitionSpeed={transitionSpeed}
        opacity={state === 'opening' || state === 'open' ? 1 : 0}
        alpha={alpha}
        variant={variant}
        position={position}
        onTransitionEnd={onTransitionEnd}
        ref={ref}
        {...restProps}
      >
        {children}
      </Flex>
    );
  }
);

export default Backdrop;
